import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { BarChart2 } from 'lucide-react';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { EngagementStats } from '@/components/performance/EngagementStats';
import { WinnerBadge } from '@/components/performance/WinnerBadge';
import { PERFORMANCE_LIST_LIMIT, PERFORMANCE_POLL_MS } from '@/constants/performance';
import { api } from '@/lib/api';
import { formatRelative, shortId } from '@/lib/format';
import type { ContentPerformance } from '@/types/engagement';

export function PerformanceListPage() {
  const [q, setQ] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['performance'],
    queryFn: () => api.fetchPerformance({ limit: PERFORMANCE_LIST_LIMIT }),
    refetchInterval: PERFORMANCE_POLL_MS,
  });

  const filtered = useMemo(() => {
    const items = data?.items ?? [];
    if (!q) return items;
    const lq = q.toLowerCase();
    return items.filter(
      (p) =>
        p.content_id.toLowerCase().includes(lq) ||
        p.persona_id.toLowerCase().includes(lq) ||
        (p.post_url ?? '').toLowerCase().includes(lq),
    );
  }, [data, q]);

  const sorted = useMemo(
    () => [...filtered].sort((a, b) => viewsOf(b) - viewsOf(a)),
    [filtered],
  );

  const winnerThreshold = data?.winner_threshold_views ?? 0;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <PageHeader />
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-14 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (!data || data.items.length === 0) {
    return (
      <div className="space-y-4">
        <PageHeader />
        <EmptyState
          icon={<BarChart2 className="h-8 w-8" />}
          title="No performance data yet"
          description="Published posts will appear here 24 hours after going live."
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader />
      <div className="flex items-center gap-3">
        <Input
          className="max-w-xs"
          placeholder="Filter by content or persona ID…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        {winnerThreshold > 0 && (
          <p className="text-xs text-muted-foreground">
            Winner threshold: {winnerThreshold.toLocaleString()} views (P75)
          </p>
        )}
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs text-muted-foreground">
              <th className="px-4 py-2 font-medium">Content</th>
              <th className="px-4 py-2 font-medium">Engagement</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 font-medium">Scraped</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((item) => (
              <PerformanceRow key={item.content_id} item={item} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PerformanceRow({ item }: { item: ContentPerformance }) {
  return (
    <tr className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
      <td className="px-4 py-3">
        <Link
          to="/performance/$contentId"
          params={{ contentId: item.content_id }}
          className="font-mono text-xs text-foreground hover:underline"
        >
          {shortId(item.content_id)}
        </Link>
        {item.post_url && (
          <a
            href={item.post_url}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-2 text-xs text-muted-foreground hover:text-foreground"
          >
            ↗ IG
          </a>
        )}
      </td>
      <td className="px-4 py-3">
        {item.latest_snapshot ? (
          <EngagementStats snapshot={item.latest_snapshot} />
        ) : (
          <span className="text-xs text-muted-foreground">Pending first scrape</span>
        )}
      </td>
      <td className="px-4 py-3">
        <WinnerBadge isWinner={item.is_winner} />
      </td>
      <td className="px-4 py-3 text-xs text-muted-foreground">
        {item.latest_snapshot
          ? formatRelative(item.latest_snapshot.scraped_at)
          : '—'}
      </td>
    </tr>
  );
}

function PageHeader() {
  return (
    <div>
      <h1 className="text-lg font-semibold">Performance</h1>
      <p className="text-sm text-muted-foreground">
        Engagement metrics scraped 1h after each post, then refreshed every 6h. Top 25% by views earn a winner badge.
      </p>
    </div>
  );
}

function viewsOf(item: ContentPerformance): number {
  return item.latest_snapshot?.views ?? 0;
}
